import styled from 'styled-components';
import { Option, SearchInput } from '../../shared/Search/SearchInput';
import { useAllIngredients, useAllProducts } from '../../apis/products-api';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  dedupeByWarehouseItemId,
  isIngredientSelectable,
  isProductSelectable,
} from '../../shared/utils/outOfStockFilters';
import { VISIBLE_INGREDIENT_CATEGORIES, VISIBLE_PRODUCT_CATEGORIES } from './OutOfStockShared';

type SearchOption = Option & {
  isProduct: boolean;
  id: number;
};

const NewOutOfStockItemSearch: React.FC = () => {
  const { data: ingredients } = useAllIngredients();
  const { data: products } = useAllProducts();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Derived directly from the loaded catalogs — no effect/state needed, so the options recompute in
  // the same render the data arrives rather than a render later.
  const values = useMemo<SearchOption[]>(() => {
    const ingredientValues: SearchOption[] = dedupeByWarehouseItemId(
      (ingredients ?? [])
        .filter((i) => i.ingredientCategoryId && VISIBLE_INGREDIENT_CATEGORIES.includes(i.ingredientCategoryId))
        .filter(isIngredientSelectable)
    ).map((i) => ({
      value: `i-${i.ingredientId}`,
      id: Number(i.ingredientId),
      label: `${i.name} (${t('ingredient')})`,
      isProduct: false,
    }));

    const productValues: SearchOption[] =
      products
        ?.filter((p) => p.productCategoryId && VISIBLE_PRODUCT_CATEGORIES.includes(p.productCategoryId))
        .filter(isProductSelectable)
        .map((p) => ({
          value: `p-${p.productId}`,
          label: `${p.name} (${t('product')})`,
          id: Number(p.productId),
          isProduct: true,
        })) ?? [];

    return [...productValues, ...ingredientValues];
  }, [ingredients, products, t]);

  const handleSelect = (value: string) => {
    const item = values.find((v) => v.value === value);
    if (!item) return;
    navigate(item.isProduct ? `/out-of-stock/product/${item.id}` : `/out-of-stock/ingredient/${item.id}`);
  };

  return (
    <Container>
      <SearchInput
        values={values}
        noLabelPrefix={true}
        onSelect={handleSelect}
        optionsLimit={10}
        label={t('Mark as out of stock')}
      />
    </Container>
  );
};

export default NewOutOfStockItemSearch;

const Container = styled.div`
  min-width: 250px;
  width: 100%;
  max-width: 450px;
`;
